import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    MenuItem,
    Select,
    CircularProgress,
    Avatar,
    Container
} from '@mui/material';
import {
    ArrowBackIosNewOutlined,
    CalendarMonthOutlined,
    RefreshRounded,
    KeyboardDoubleArrowLeft,
    KeyboardDoubleArrowRight
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { getTargetMatrixApi, getTargetMatrixV2Api } from 'utils/API/Mis Apis/HrPortelApis';
import { useMemo } from 'react';

// ─── Constants ───────────────────────────────────────────────────
const FINANCIAL_YEARS = Array.from({ length: 14 }, (_, i) => {
    const startYear = 2025 + i;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
});

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

const ROLES = [
    { value: 'manager', label: 'Frontline' },
    { value: 'middle_manager', label: 'Team Leader' },
    { value: 'state_head', label: 'State Head' },
    { value: 'zone_head', label: 'Zone Head' },
    { value: 'national_head', label: 'National Head' }
];

const ALL_METRIC_CONFIGS = {
    GTV: { label: 'GTV (₹)', color: '#6366f1' },
    BC: { label: 'BC', color: '#14b8a6' },
    DIST: { label: 'Dist.', color: '#f59e0b' },
    MATM: { label: 'MATM', color: '#ec4899' },
    POS: { label: 'POS', color: '#f97316' },
    Aggregator: { label: 'Aggr.', color: '#8b5cf6' }
};

const HIERARCHY_ORDER = [
    { id: 'NH', value: 'national_head', level: 0 },
    { id: 'ZH', value: 'zone_head', level: 1 },
    { id: 'SH', value: 'state_head', level: 2 },
    { id: 'TL', value: 'middle_manager', level: 3 }, // Team Leader
    { id: 'FL', value: 'manager', level: 4 } // Frontline
];

const HIERARCHY_THEMES = [
    { name: 'Blue', h: 200, s: 54, l: 40 },
    { name: 'Emerald', h: 164, s: 86, l: 26 },
    { name: 'Purple', h: 263, s: 67, l: 35 },
    { name: 'Rose', h: 341, s: 76, l: 30 },
    { name: 'Amber', h: 21, s: 78, l: 27 }
];

const getDynamicShade = (groupIndex, verticalIndex) => {
    const theme = HIERARCHY_THEMES[groupIndex % HIERARCHY_THEMES.length];
    // Start with a visible light shade (88%) instead of near-white (95%)
    const l = Math.max(theme.l, 88 - verticalIndex * 9);
    const s = Math.min(theme.s, 25 + verticalIndex * 8);
    const isLight = l > 65;
    return {
        bg: `hsl(${theme.h}, ${s}%, ${l}%)`,
        text: isLight ? '#0f172a' : '#fff',
        avatarBg: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'
    };
};

// Toggle this for testing the new nested API structure
const USE_NEW_STRUCTURE = true;
// Flatten nested API structure while calculating rowSpans for visual hierarchy
const flattenMatrixData = (data) => {
    const results = [];
    const roleToInfo = {
        national_head: { id: 'NH', level: 0 },
        zone_head: { id: 'ZH', level: 1 },
        state_head: { id: 'SH', level: 2 },
        middle_manager: { id: 'TL', level: 3 },
        manager: { id: 'FL', level: 4 }
    };

    // First pass: Calculate rowSpans recursively
    const calculateSpans = (node) => {
        if (!node.children || node.children.length === 0) {
            node.rowSpan = 1;
            return 1;
        }
        let totalSpan = 0;
        node.children.forEach((child) => {
            totalSpan += calculateSpans(child);
        });
        node.rowSpan = totalSpan;
        return totalSpan;
    };

    // Second pass: Flatten into rows with span indicators and group/level info
    const traverse = (node, groupIndex, verticalCounters, currentPath = {}) => {
        const info = roleToInfo[node.role];
        const nodePath = { ...currentPath };

        if (info) {
            // Track vertical index for each level within the group
            const vIndex = verticalCounters[info.id] || 0;
            nodePath[info.id] = {
                name: node.name,
                rowSpan: node.rowSpan,
                isStart: true,
                groupIndex: groupIndex,
                verticalIndex: vIndex
            };
            verticalCounters[info.id] = vIndex + 1;
        }

        if (!node.children || node.children.length === 0) {
            results.push({
                ...nodePath,
                months: node.months || {},
                isGroupStart: results.length === 0 || (info?.id === 'NH' && node.rowSpan > 0)
            });
            return;
        }

        node.children.forEach((child, idx) => {
            const childContext = { ...nodePath };
            if (idx > 0) {
                // Mark all ancestors as not starting here for branched rows
                Object.keys(childContext).forEach((key) => {
                    childContext[key] = { ...childContext[key], isStart: false };
                });
            }
            traverse(child, groupIndex, verticalCounters, childContext);
        });
    };

    if (Array.isArray(data)) {
        data.forEach((item, idx) => {
            calculateSpans(item);
            traverse(item, idx % HIERARCHY_THEMES.length, {});
        });
    }
    return results;
};

const TargetMatrix = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [matrixData, setMatrixData] = useState([]);
    const [financialYear, setFinancialYear] = useState(FINANCIAL_YEARS[0]);
    const [selectedRole, setSelectedRole] = useState(ROLES[0].value);
    const [collapsedRoles, setCollapsedRoles] = useState(new Set());

    const toggleColumn = (roleId) => {
        const newCollapsed = new Set(collapsedRoles);
        if (newCollapsed.has(roleId)) {
            newCollapsed.delete(roleId);
        } else {
            newCollapsed.add(roleId);
        }
        setCollapsedRoles(newCollapsed);
    };

    const visibleHierarchy = useMemo(() => {
        const selectedIdx = HIERARCHY_ORDER.findIndex((r) => r.value === selectedRole);
        if (selectedIdx === -1) return HIERARCHY_ORDER;
        return HIERARCHY_ORDER.slice(0, selectedIdx + 1).filter((role) => !collapsedRoles.has(role.id));
    }, [selectedRole, collapsedRoles]);

    const hiddenHierarchyCount = useMemo(() => {
        const selectedIdx = HIERARCHY_ORDER.findIndex((r) => r.value === selectedRole);
        if (selectedIdx === -1) return 0;
        return HIERARCHY_ORDER.slice(0, selectedIdx + 1).filter((role) => collapsedRoles.has(role.id)).length;
    }, [selectedRole, collapsedRoles]);

    const dynamicMetrics = useMemo(() => {
        const uniqueKeys = new Set();
        matrixData.forEach((row) => {
            if (row.months) {
                Object.values(row.months).forEach((monData) => {
                    Object.keys(monData).forEach((key) => uniqueKeys.add(key));
                });
            }
        });
        return Array.from(uniqueKeys).map((key) => ({
            id: key,
            label: ALL_METRIC_CONFIGS[key]?.label || key,
            color: ALL_METRIC_CONFIGS[key]?.color || '#64748b'
        }));
    }, [matrixData]);

    const fetchData = useCallback(async (year, role) => {
        setLoading(true);
        try {
            const response = await (USE_NEW_STRUCTURE ? getTargetMatrixV2Api : getTargetMatrixApi)({
                financial_year: year,
                role: role
            });

            if (response?.data?.success) {
                const rawData = response.data.data;

                // Transform data if it's the new nested structure
                if (USE_NEW_STRUCTURE && Array.isArray(rawData) && rawData.length > 0 && (rawData[0].children || rawData[0].role)) {
                    setMatrixData(flattenMatrixData(rawData));
                } else {
                    setMatrixData(rawData);
                }
            }
        } catch (error) {
            console.error('Fetch matrix error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(financialYear, selectedRole);
    }, [fetchData, financialYear, selectedRole]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Vibrant Enterprise Blue
                    px: { xs: 2, md: 4 },
                    pt: 3,
                    pb: 8,
                    color: '#fff',
                    borderRadius: '10px'
                }}
            >
                <Container maxWidth="xl">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <IconButton
                            onClick={() => navigate('/HR/target-settings')}
                            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBackIosNewOutlined />
                        </IconButton>
                        <Box>
                            <Typography variant="h3" fontWeight={900} color="#fff" letterSpacing="-0.02em">
                                Target Matrix
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7, color: '#fff' }}>
                                Comparative monthly breakdown of organizational performance benchmarks
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            flexWrap: 'wrap'
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CalendarMonthOutlined sx={{ color: '#3b82f6' }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#fff">
                                Role:
                            </Typography>
                            <Select
                                size="small"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                sx={{
                                    color: '#fff',
                                    minWidth: 150,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                                    '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                }}
                            >
                                {ROLES.map((role) => (
                                    <MenuItem key={role.value} value={role.value}>
                                        {role.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CalendarMonthOutlined sx={{ color: '#3b82f6' }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#fff">
                                Period:
                            </Typography>
                            <Select
                                size="small"
                                value={financialYear}
                                onChange={(e) => setFinancialYear(e.target.value)}
                                sx={{
                                    color: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                                    '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                }}
                            >
                                {FINANCIAL_YEARS.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        <IconButton onClick={() => fetchData(financialYear, selectedRole)} sx={{ color: '#fff' }}>
                            <RefreshRounded />
                        </IconButton>
                    </Paper>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ mt: -4 }}>
                <AnimatePresence mode="wait">
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={60} thickness={4} sx={{ color: '#1e293b' }} />
                        </Box>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0 20px 50px -12px rgba(0,0,0,0.08)'
                                }}
                            >
                                <TableContainer sx={{ maxHeight: 'calc(200vh - 300px)' }}>
                                    <Table stickyHeader aria-label="target matrix table">
                                        <TableHead>
                                            <TableRow>
                                                {/* Hierarchy Headers */}
                                                {visibleHierarchy.map((role) => (
                                                    <TableCell
                                                        key={role.id}
                                                        rowSpan={2}
                                                        sx={{
                                                            bgcolor: '#f1f5f9',
                                                            fontWeight: 900,
                                                            fontSize: '0.85rem',
                                                            color: '#0f172a',
                                                            zIndex: 13,
                                                            position: 'sticky',
                                                            top: 0,
                                                            borderRight: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                        >
                                                            {role.id}
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleColumn(role.id)}
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                <KeyboardDoubleArrowLeft sx={{ fontSize: '1rem' }} />
                                                            </IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                ))}

                                                {/* Restore Button if Columns are Hidden */}
                                                {hiddenHierarchyCount > 0 && (
                                                    <TableCell
                                                        rowSpan={2}
                                                        sx={{
                                                            bgcolor: '#f1f5f9',
                                                            zIndex: 13,
                                                            position: 'sticky',
                                                            top: 0,
                                                            width: 40,
                                                            p: 0,
                                                            textAlign: 'center',
                                                            borderRight: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setCollapsedRoles(new Set())}
                                                            title="Restore All Columns"
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <KeyboardDoubleArrowRight sx={{ fontSize: '1.2rem' }} />
                                                        </IconButton>
                                                    </TableCell>
                                                )}

                                                {/* Monthly Metric Headers */}
                                                {MONTHS.map((month) => (
                                                    <TableCell
                                                        key={month}
                                                        align="center"
                                                        colSpan={dynamicMetrics.length}
                                                        sx={{
                                                            bgcolor: '#1e3a8a', // Corporate Blue
                                                            color: '#fff',
                                                            fontWeight: 900,
                                                            fontSize: '0.9rem',
                                                            borderLeft: '1px solid rgba(255,255,255,0.2)',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            top: 0,
                                                            zIndex: 12
                                                        }}
                                                    >
                                                        {month}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            <TableRow>
                                                {/* Metric labels for each month */}
                                                {MONTHS.map((month) => (
                                                    <METRIC_HEADER_ROW key={month} metrics={dynamicMetrics} />
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matrixData.map((row, idx) => {
                                                const nhData = row['NH'];
                                                const isNewNH = nhData && nhData.isStart;
                                                return (
                                                    <TableRow
                                                        key={idx}
                                                        hover
                                                        sx={{
                                                            borderTop: isNewNH && idx !== 0 ? '3px solid #cbd5e1' : 'none',
                                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.02) !important' }
                                                        }}
                                                    >
                                                        <HIERARCHY_CELLS row={row} visibleHierarchy={visibleHierarchy} />
                                                        {MONTHS.map((month) => (
                                                            <MONTH_DATA_CELLS
                                                                key={month}
                                                                data={row.months?.[month] || row['0']?.[month]}
                                                                metrics={dynamicMetrics}
                                                            />
                                                        ))}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Container>
        </Box>
    );
};

const METRIC_HEADER_ROW = ({ metrics }) => (
    <>
        {metrics.map((metric) => (
            <TableCell
                key={metric.id}
                align="center"
                sx={{
                    fontSize: '0.75rem',
                    fontWeight: 900,
                    color: '#0f172a', // Higher contrast dark blue/slate
                    bgcolor: '#f1f5f9', // Slightly darker background for the sub-header
                    minWidth: 90,
                    px: 1,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 45, // Height of the first row
                    zIndex: 12
                }}
            >
                {metric.label}
            </TableCell>
        ))}
    </>
);

METRIC_HEADER_ROW.propTypes = {
    metrics: PropTypes.array.isRequired
};

const HIERARCHY_CELLS = ({ row, visibleHierarchy }) => (
    <>
        {visibleHierarchy.map((role) => {
            const cellData = row[role.id];
            // If it's the simplified data (non-V2), render normally
            if (!cellData || typeof cellData === 'string') {
                return (
                    <TableCell key={role.id} sx={{ borderRight: '1px solid #e2e8f0', minWidth: 150 }}>
                        {cellData && cellData !== 'N/A' ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.50', color: 'primary.main' }}>
                                    {cellData.charAt(0)}
                                </Avatar>
                                <Typography variant="caption" fontWeight={700} noWrap>
                                    {cellData}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                -
                            </Typography>
                        )}
                    </TableCell>
                );
            }

            // V2 Data with thematic rowSpans (Dynamic Vertical Shading)
            if (!cellData.isStart) return null;

            const shade = getDynamicShade(cellData.groupIndex, cellData.verticalIndex);

            return (
                <TableCell
                    key={role.id}
                    rowSpan={cellData.rowSpan || 1}
                    sx={{
                        borderRight: '1px solid #e2e8f0',
                        minWidth: 150,
                        verticalAlign: 'top',
                        bgcolor: shade.bg,
                        transition: 'all 0.2s ease'
                    }}
                >
                    {cellData.name && cellData.name !== 'N/A' && cellData.name !== 'null' ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                                sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: shade.avatarBg,
                                    color: shade.text,
                                    border: `1px solid ${shade.text}33`
                                }}
                            >
                                {cellData.name.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography
                                    variant="caption"
                                    fontWeight={800}
                                    sx={{ color: shade.text, display: 'block', lineHeight: 1.2 }}
                                >
                                    {cellData.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: shade.text, opacity: 0.7, fontSize: '0.65rem' }}>
                                    {role.id}
                                </Typography>
                            </Box>
                        </Stack>
                    ) : (
                        <Typography variant="caption" sx={{ color: shade.text, opacity: 0.5, fontStyle: 'italic' }}>
                            -
                        </Typography>
                    )}
                </TableCell>
            );
        })}
    </>
);

HIERARCHY_CELLS.propTypes = {
    row: PropTypes.object.isRequired,
    visibleHierarchy: PropTypes.array.isRequired
};

const MONTH_DATA_CELLS = ({ data, metrics }) => {
    return (
        <>
            {metrics.map((metric, idx) => (
                <TableCell
                    key={metric.id}
                    align="center"
                    sx={{
                        borderLeft: idx === 0 ? '1px solid #e2e8f0' : 'none',
                        px: 1
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={800}
                        sx={{
                            color: data && parseFloat(data[metric.id]) > 0 ? 'text.primary' : 'text.disabled',
                            display: 'block'
                        }}
                    >
                        {data && data[metric.id] !== undefined
                            ? metric.id === 'GTV'
                                ? `₹${Math.round(parseFloat(data[metric.id])).toLocaleString()}`
                                : data[metric.id]
                            : '-'}
                    </Typography>
                </TableCell>
            ))}
        </>
    );
};

MONTH_DATA_CELLS.propTypes = {
    data: PropTypes.object,
    metrics: PropTypes.array.isRequired
};

export default TargetMatrix;
